import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { getStoredTokens, decodeJWT } from '../utils/auth';
import { API_BASE_URL } from '../constants/apiConstants';
import './CSS/Profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({ name: '', email: '', userId: '' });
  const [fbAccounts, setFbAccounts] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [allInstagramAccounts, setAllInstagramAccounts] = useState([]);
  const [connectedAccounts, setConnectedAccounts] = useState({
    facebook: 0,
    instagram: 0,
    linkedin: 0,
    pinterest: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get user info from token
    const tokens = getStoredTokens();
    if (tokens?.idToken) {
      try {
        const decoded = decodeJWT(tokens.idToken);
        setUserInfo({
          name: decoded.name || decoded.email?.split('@')[0] || 'User',
          email: decoded.email || '',
          userId: decoded.sub || localStorage.getItem('userId') || 'test-user-123'
        });
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }

    // Fetch connected accounts
    fetchConnectedAccounts();
  }, []);

  const fetchConnectedAccounts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/meta/data`, {
        headers: {
          'userid': localStorage.getItem('userId') || 'test-user-123'
        }
      });

      if (response.data.success) {
        const data = response.data.data;
        const token = data.token || null;
        const accounts = token ? [{
          facebook_user_id: token.facebook_user_id,
          facebook_name: token.facebook_name,
          facebook_email: token.facebook_email,
          facebook_picture: token.facebook_picture
        }] : [];
        const pages = data.facebook_pages || [];
        const igAccounts = data.instagram_accounts || [];
        
        setFbAccounts(accounts);
        setAllPages(pages);
        setAllInstagramAccounts(igAccounts);
        
        setConnectedAccounts({
          facebook: pages.length,
          instagram: igAccounts.length,
          linkedin: 0,
          pinterest: 0
        });
      }
    } catch (error) {
      console.error('Error fetching connected accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getTotalConnected = () => {
    return Object.values(connectedAccounts).reduce((sum, count) => sum + count, 0);
  };

  const getAccountPageCount = () => {
    return allPages.length;
  };

  const getAccountInstagramCount = () => {
    return allInstagramAccounts.length;
  };

  return (
    <div className="profile-page">
      <Navbar />
      <div className="profile-container">
        <div className="profile-header">
          <h1>My Profile</h1>
          <p className="profile-subtitle">Manage your account and connected platforms</p>
        </div>

        <div className="profile-content">
          {/* User Information Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <h2>User Information</h2>
            </div>
            <div className="profile-card-body">
              <div className="user-avatar-large">
                {getInitials(userInfo.name)}
              </div>
              <div className="user-details">
                <div className="user-detail-item">
                  <span className="detail-label">Name</span>
                  <span className="detail-value">{userInfo.name}</span>
                </div>
                <div className="user-detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{userInfo.email}</span>
                </div>
                <div className="user-detail-item">
                  <span className="detail-label">User ID</span>
                  <span className="detail-value detail-value-small">{userInfo.userId}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Connected Accounts Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <h2>Connected Accounts</h2>
              <span className="connected-count">
                {getTotalConnected()} {getTotalConnected() === 1 ? 'account' : 'accounts'} connected
              </span>
            </div>
            <div className="profile-card-body">
              {loading ? (
                <div className="accounts-loading">
                  <div className="spinner-small"></div>
                  <p>Loading connected accounts...</p>
                </div>
              ) : (
                <div className="connected-accounts-list">
                  {/* Facebook Accounts - Show each account separately */}
                  {fbAccounts.length > 0 ? (
                    fbAccounts.map((fbAccount) => {
                      const pageCount = getAccountPageCount();
                      const igCount = getAccountInstagramCount();
                      
                      return (
                        <div key={fbAccount.facebook_user_id} className="connected-account-group">
                          <div className="account-group-header">
                            <img 
                              src={fbAccount.facebook_picture} 
                              alt={fbAccount.facebook_name}
                              className="account-group-avatar"
                            />
                            <div className="account-group-info">
                              <div className="account-group-name">{fbAccount.facebook_name}</div>
                              <div className="account-group-email">{fbAccount.facebook_email}</div>
                            </div>
                          </div>
                          
                          {/* Facebook Pages */}
                          <div className="connected-account-item nested">
                            <div className="account-icon facebook">
                              📘
                            </div>
                            <div className="account-details">
                              <div className="account-name">Facebook Pages</div>
                              <div className="account-status">
                                {pageCount > 0 ? (
                                  <>
                                    <span className="status-dot status-connected"></span>
                                    <span className="status-text">
                                      {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="status-dot status-disconnected"></span>
                                    <span className="status-text">No pages</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Instagram for this account */}
                          <div className="connected-account-item nested">
                            <div className="account-icon instagram">
                              📸
                            </div>
                            <div className="account-details">
                              <div className="account-name">Instagram</div>
                              <div className="account-status">
                                {igCount > 0 ? (
                                  <>
                                    <span className="status-dot status-connected"></span>
                                    <span className="status-text">
                                      {igCount} {igCount === 1 ? 'account' : 'accounts'}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="status-dot status-disconnected"></span>
                                    <span className="status-text">No Instagram linked</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="connected-account-item">
                      <div className="account-icon facebook">
                        📘
                      </div>
                      <div className="account-details">
                        <div className="account-name">Facebook</div>
                        <div className="account-status">
                          <span className="status-dot status-disconnected"></span>
                          <span className="status-text">Not connected</span>
                        </div>
                      </div>
                      <button 
                        className="account-action-btn"
                        onClick={() => navigate('/social-media-automation')}
                      >
                        Connect
                      </button>
                    </div>
                  )}

                  {/* Manage Button */}
                  {fbAccounts.length > 0 && (
                    <div className="account-manage-section">
                      <button 
                        className="account-manage-btn"
                        onClick={() => navigate('/social-media-automation')}
                      >
                        Manage All Accounts
                      </button>
                    </div>
                  )}

                  {/* LinkedIn */}
                  <div className="connected-account-item disabled">
                    <div className="account-icon linkedin">
                      💼
                    </div>
                    <div className="account-details">
                      <div className="account-name">LinkedIn</div>
                      <div className="account-status">
                        <span className="status-dot status-disconnected"></span>
                        <span className="status-text">Coming soon</span>
                      </div>
                    </div>
                    <button className="account-action-btn" disabled>
                      Coming Soon
                    </button>
                  </div>

                  {/* Pinterest */}
                  <div className="connected-account-item disabled">
                    <div className="account-icon pinterest">
                      📌
                    </div>
                    <div className="account-details">
                      <div className="account-name">Pinterest</div>
                      <div className="account-status">
                        <span className="status-dot status-disconnected"></span>
                        <span className="status-text">Coming soon</span>
                      </div>
                    </div>
                    <button className="account-action-btn" disabled>
                      Coming Soon
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="profile-card">
            <div className="profile-card-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="profile-card-body">
              <div className="quick-actions-grid">
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate('/social-media-automation')}
                >
                  <span className="action-icon">🔗</span>
                  <span className="action-text">Connect Accounts</span>
                </button>
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate('/business-dna')}
                >
                  <span className="action-icon">🧬</span>
                  <span className="action-text">Business DNA</span>
                </button>
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate('/settings')}
                >
                  <span className="action-icon">⚙️</span>
                  <span className="action-text">Settings</span>
                </button>
                <button 
                  className="quick-action-btn"
                  onClick={() => navigate('/adflyn-chatbot')}
                >
                  <span className="action-icon">🤖</span>
                  <span className="action-text">AI Chatbot</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
